import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, Zap } from 'lucide-react';

interface DeleteSurgePricingConfirmationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  surgePricingDetails: string;
  surgePricingId: number;
  onConfirm: (surgePricingId: number) => void;
  isLoading?: boolean;
}

const DeleteSurgePricingConfirmation: React.FC<DeleteSurgePricingConfirmationProps> = ({
  open,
  onOpenChange,
  surgePricingDetails,
  surgePricingId,
  onConfirm,
  isLoading = false,
}) => {
  const handleConfirm = () => {
    onConfirm(surgePricingId);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" />
            Delete Surge Pricing Rule
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p>
                Are you sure you want to delete this surge pricing rule? This action cannot be undone.
              </p>

              <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-destructive" />
                  <span className="font-medium">Rule Details:</span>
                </div>
                <p className="text-sm text-destructive font-medium">
                  {surgePricingDetails}
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Rule ID: {surgePricingId}
                </p>
              </div>

              <div className="p-4 bg-muted/50 rounded-lg">
                <h4 className="font-medium mb-2 text-warning">Impact:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• This surge pricing rule will be permanently removed</li>
                  <li>• No future rides will use this surge multiplier</li>
                  <li>• The vehicle category will use base pricing during this time</li>
                  <li>• This may affect driver earnings during peak hours</li>
                </ul>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isLoading ? 'Deleting...' : 'Delete Surge Rule'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteSurgePricingConfirmation;